import { HandleReturnItemChange } from 'components/Orders/Order';
import Flex from 'components/ui/Flex';
import Input from 'components/ui/Input';
import Text from 'components/ui/Text';
import { CellInputRefs } from 'hooks/useKeyboardNavigation';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ReturnItem, ReturnOrderLine } from 'returnCenter-api';
import { ExtendedReturnItem } from 'store/reducers/shared/sliceTypes';

import { styled } from 'styled-components';

export function getTotalReturning(items: ExtendedReturnItem[]): number {
    return items.reduce((sum, item) => sum + (item.quantityInReturn ?? 0), 0);
}

type ReturnQtyCellProps = {
    item: ReturnItem;
    itemId?: number | string;
    rowInputRefs?: React.MutableRefObject<{ [key: string]: HTMLInputElement | null }>;
    handleReturnItemChange?: HandleReturnItemChange;
    orderline: ReturnOrderLine;
    disabled?: boolean;
    rowIndex?: string | number;
    colIndex?: string | number;
    cellInputRefs?: CellInputRefs;
    handleArrowKeyDown?: (e: any, orderId: any, rowIndex: any, colIndex: any) => void;
    cellRefPrefix?: string;
};

const ReturnQtyCell = ({
    itemId,
    item,
    rowInputRefs,
    handleReturnItemChange,
    orderline,
    rowIndex,
    colIndex,
    cellInputRefs,
    handleArrowKeyDown,
    cellRefPrefix = '',
    disabled = false,
}: ReturnQtyCellProps) => {
    const { t } = useTranslation();

    const availableQty = orderline?.quantityAvailableForReturn ?? 0;
    const totalReturning = getTotalReturning((orderline?.returnItems ?? []) as ExtendedReturnItem[]);
    const remainingQty = availableQty - totalReturning;

    if (disabled) {
        return (
            <Flex align="center" gap="0.2rem" className="quantity-wrapper">
                <Text fontSize={12} bold as="span">
                    {item.quantityInReturn ?? 0}
                </Text>
                <RemainingText fontSize={12} as="span" bold $depleted={remainingQty <= 0}>
                    {`/ ${remainingQty}`}
                </RemainingText>
            </Flex>
        );
    }

    // Helper to clamp value
    const clampQuantity = (value: number) => {
        const totalQtyInReturnItems = orderline?.returnItems?.reduce((sum, item: ExtendedReturnItem) => {
            if (item.id === itemId || itemId === item.splitId) {
                return sum;
            }
            return sum + (item.quantityInReturn || 0);
        }, 0);

        const max =
            (orderline.quantityAvailableForReturn &&
                orderline?.quantityAvailableForReturn - (totalQtyInReturnItems || 0)) ||
            0;

        if (value > max) {
            toast.error(t('cannotReturnMoreThanShipped', { max: max, sku: orderline?.sku ?? '' }), {
                autoClose: 3000,
                closeOnClick: true,
            });
            return max;
        }

        return value;
    };

    return (
        <Flex align="center" gap="0.2rem" className="quantity-wrapper">
            <StyledNumberInput
                data-allow-paste
                disabled={disabled}
                data-input
                data-row={rowIndex}
                data-row-cell={colIndex}
                data-item-id={itemId}
                textAlignCenter
                key={itemId}
                max={orderline?.quantityShipped ?? 0}
                rounded
                placeholder={t('quantity')}
                value={item.quantityInReturn ?? 0}
                ref={(el) => {
                    if (rowInputRefs) {
                        if (el && orderline?.ean) rowInputRefs.current[orderline.ean] = el;
                        if (el && orderline?.sku) rowInputRefs.current[orderline.sku] = el;
                        if (el && orderline?.id != null) rowInputRefs.current[String(orderline.id)] = el;
                    }
                    const key = `${cellRefPrefix}${rowIndex}-${colIndex}`;
                    if (cellInputRefs) cellInputRefs.current[key] = el;
                }}
                onChange={(e) => {
                    const inputValue = e.target.value;
                    let numValue = Number(inputValue);
                    if (isNaN(numValue) || numValue < 0) return;
                    numValue = clampQuantity(numValue);

                    handleReturnItemChange?.(
                        orderline?.orderNumber ?? '',
                        orderline?.sku ?? '',
                        orderline?.orderLineNumber ?? '',
                        itemId ?? -1,
                        'quantityInReturn',
                        numValue,
                    );
                }}
                onFocus={(e) => {
                    e.target.select();
                }}
                tabIndex={0}
                onKeyDown={handleArrowKeyDown ? (e) => handleArrowKeyDown(e, itemId, rowIndex, colIndex) : undefined}
            />
            <RemainingText
                fontSize={12}
                as="span"
                bold
                $depleted={remainingQty <= 0}
            >{`/ ${remainingQty}`}</RemainingText>
        </Flex>
    );
};

export default ReturnQtyCell;

const RemainingText = styled(Text)<{ $depleted?: boolean }>`
    white-space: nowrap;
    color: ${({ $depleted, theme }) => ($depleted ? theme.colors.textFaded : 'inherit')};
    width: 27px;
`;

const StyledNumberInput = styled(Input)<{ underline?: boolean; fullWidth?: boolean; textAlignCenter?: boolean }>`
    width: 100%;
    flex: 1;
    input {
        height: 38px !important;
        width: ${({ fullWidth }) => (fullWidth ? '100% !important;' : '55px !important;')};
        text-align: ${({ textAlignCenter }) => textAlignCenter && 'center;'};
        padding-left: 0px !important;
    }

    > * {
        font-size: 11px !important;
    }
`;
